import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  ListAlt as ItemsIcon,
  Category as CategoryIcon,
  ReceiptLong as CounterIcon,
  Person as CaptainIcon,
  AccountBalanceWallet as SalesIcon,
  DashboardRounded as DashboardIcon,
} from '@mui/icons-material';
import { useAppStore } from '../../store/appStore';

const Reports = () => {
  const navigate = useNavigate();
  const { branch: selectedBranch } = useAppStore();

  const formatDate = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.set('start_date', formatDate(new Date()));
    params.set('end_date', formatDate(new Date()));
    params.set('from_time', '06:00');
    params.set('to_time', '22:00');

    const branchId = selectedBranch?.isAllOutlets || selectedBranch?.id === 'all-outlets'
      ? 'all'
      : (selectedBranch?.id ? String(selectedBranch.id) : 'all');

    params.set('branch_id', branchId);
    return params.toString();
  };

  const handleNavigate = (path) => {
    const q = buildQuery();
    navigate({ pathname: path, search: q ? `?${q}` : '' });
  };

  const reportCards = [
    {
      title: 'Sales Summary',
      description: 'Sales aggregated by category',
      icon: <BarChartIcon sx={{ fontSize: 34 }} />,
      path: '/reports/sales-summary',
      color: 'primary',
    },
    {
      title: 'Items Summary',
      description: 'Top selling items and metrics',
      icon: <ItemsIcon sx={{ fontSize: 34 }} />,
      path: '/reports/items-summary',
      color: 'success',
    },
    {
      title: 'Category Summary',
      description: 'Category level aggregates and trends',
      icon: <CategoryIcon sx={{ fontSize: 34 }} />,
      path: '/reports/category-summary',
      color: 'info',
    },
    {
      title: 'Billing Type Summary',
      description: 'Orders and sales grouped by billing type',
      icon: <CounterIcon sx={{ fontSize: 34 }} />,
      path: '/reports/billing-type-summary',
      color: 'secondary',
    },
    {
      title: 'Captain Wise Summary',
      description: 'Performance by captain',
      icon: <CaptainIcon sx={{ fontSize: 34 }} />,
      path: '/reports/captain-wise-summary',
      color: 'warning',
    },
    {
      title: 'Sales Report',
      description: 'Payments and sales breakdown',
      icon: <SalesIcon sx={{ fontSize: 34 }} />,
      path: '/reports/sales-report',
      color: 'error',
    },
    {
      title: 'Payment Summary',
      description: 'Payment-wise sales summary',
      icon: <SalesIcon sx={{ fontSize: 34 }} />,
      path: '/reports/payment-summary',
      color: 'error',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.04em' }}>Reports</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          Generate and explore sales, category, and item insights from your POS data
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {reportCards.map((r) => (
          <Grid item xs={12} sm={6} md={4} key={r.title}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 32px rgba(15, 23, 42, 0.10)',
                },
              }}
            >
              <CardContent sx={{ textAlign: 'left', pt: 3, pb: 1.5, px: 2.5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2.5,
                    background: (theme) => theme.palette[r.color]?.main || theme.palette.primary.main,
                    color: '#fff',
                    boxShadow: '0 10px 18px rgba(15, 23, 42, 0.10)',
                    mb: 2,
                  }}
                >
                  {r.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>{r.title}</Typography>
                <Typography variant="body2" color="text.secondary">{r.description}</Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2.5, pb: 2.5, pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <DashboardIcon sx={{ fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>Overview</Typography>
                </Box>
                <Button variant="contained" color={r.color} onClick={() => handleNavigate(r.path)} sx={{ borderRadius: 2, px: 2.5 }}>
                  Open
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Reports;